import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Building, AlertCircle, Check } from 'lucide-react';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';

interface Property {
  id: number;
  address: string;
}

const CHECK_TYPES = [
  { value: 'SMOKE_ALARM', label: 'Smoke Alarm Check' },
  { value: 'GAS_SAFETY', label: 'Gas Safety Check' },
  { value: 'ELECTRICAL', label: 'Electrical Safety Check' },
  { value: 'POOL_FENCE', label: 'Pool Fence Compliance' },
  { value: 'FIRE_EXTINGUISHER', label: 'Fire Extinguisher' },
  { value: 'PEST_CONTROL', label: 'Pest Control' },
  { value: 'OTHER', label: 'Other' },
];

export default function CreateComplianceCheck() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    propertyId: '',
    checkType: '',
    expiryDate: '',
    completedDate: '',
    certificateNumber: '',
    certificateUrl: '',
    inspectorName: '',
    inspectorLicense: '',
    notes: '',
    reminderDaysBefore: '30',
  });

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await api.get<any>('/owner/properties');
      const propsList = Array.isArray(response) ? response : (response?.content || []);
      setProperties(propsList);
    } catch (err) {
      console.error('Failed to load properties:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.propertyId || !form.checkType) {
      setError('Please select a property and check type');
      return;
    }

    setSaving(true);
    try {
      await api.post('/compliance/checks', {
        propertyId: parseInt(form.propertyId),
        checkType: form.checkType,
        expiryDate: form.expiryDate || null,
        completedDate: form.completedDate || null,
        certificateNumber: form.certificateNumber || null,
        certificateUrl: form.certificateUrl || null,
        inspectorName: form.inspectorName || null,
        inspectorLicense: form.inspectorLicense || null,
        notes: form.notes || null,
        reminderDaysBefore: parseInt(form.reminderDaysBefore) || 30,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/owner/compliance');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to create compliance check');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Compliance Check Created!</h2>
          <p className="text-gray-600">Redirecting to compliance management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Header */}
      <PageHeader
        title="Add Compliance Check"
        subtitle="Record a new compliance check for your property"
        icon={<ClipboardCheck className="w-8 h-8" />}
        backPath="/owner/compliance"
      />

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Property Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Property</h2>
          </div>
          <select
            value={form.propertyId}
            onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            required
          >
            <option value="">Select a property...</option>
            {properties.map(property => (
              <option key={property.id} value={property.id}>
                {property.address}
              </option>
            ))}
          </select>
        </div>

        {/* Check Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardCheck className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Check Details</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Check Type *</label>
              <select
                value={form.checkType}
                onChange={(e) => setForm({ ...form, checkType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              >
                <option value="">Select check type...</option>
                {CHECK_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Completed Date</label>
                <input
                  type="date"
                  value={form.completedDate}
                  onChange={(e) => setForm({ ...form, completedDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                <input
                  type="date"
                  value={form.expiryDate}
                  onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Number</label>
              <input
                type="text"
                value={form.certificateNumber}
                onChange={(e) => setForm({ ...form, certificateNumber: e.target.value })}
                placeholder="e.g., CERT-2024-001"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inspector Name</label>
                <input
                  type="text"
                  value={form.inspectorName}
                  onChange={(e) => setForm({ ...form, inspectorName: e.target.value })}
                  placeholder="Inspector name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inspector License</label>
                <input
                  type="text"
                  value={form.inspectorLicense}
                  onChange={(e) => setForm({ ...form, inspectorLicense: e.target.value })}
                  placeholder="License number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reminder (days before expiry)</label>
              <input
                type="number"
                value={form.reminderDaysBefore}
                onChange={(e) => setForm({ ...form, reminderDaysBefore: e.target.value })}
                min="1"
                max="90"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Any additional notes..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/owner/compliance')}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-3 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Compliance Check'}
          </button>
        </div>
      </form>
    </div>
  );
}


